// ── TendersCard ───────────────────────────────────────────────────────────────
// A terminal sale collected in more than one piece: each tender, and what has
// come back of it.

import type { TenderLine, Transaction } from "@/application/money/types";
import { formatCurrency, parseTenders } from "@/application/money/types";

// The vocabulary the terminal prints on its receipt.
function methodLabel(fundingType: string | null | undefined): string {
  switch (fundingType) {
    case "cash":
      return "Cash";
    case "cheque":
      return "Cheque";
    case "other":
      return "Other";
    case "debit":
      return "Debit";
    case "credit":
      return "Credit";
    default:
      return "Card";
  }
}

function reversalText(
  line: TenderLine,
  currency: string | null | undefined,
): string | null {
  const back = line.reversedAmountCents ?? 0;
  if (back <= 0) return null;
  const word = line.status === "VOIDED" ? "Voided" : "Refunded";
  return back >= line.amountCents
    ? word
    : `${word} ${formatCurrency(back, currency)}`;
}

interface TendersCardProps {
  transaction: Transaction;
}

export function TendersCard({ transaction: tx }: TendersCardProps) {
  const lines = parseTenders(tx.tenders);
  if (lines.length < 2) return null;

  return (
    <div className="bg-card rounded-lg border p-5">
      <h2 className="text-foreground mb-3 text-sm font-semibold">Tenders</h2>
      <div className="divide-y text-sm">
        {lines.map((line, index) => {
          const back = reversalText(line, tx.currency);
          return (
            <div
              key={line.transactionId ?? index}
              className="flex justify-between py-2.5"
            >
              <span className="text-muted-foreground">
                {methodLabel(line.fundingType)}
              </span>
              <span className="text-foreground font-mono text-sm font-medium">
                {formatCurrency(line.amountCents, tx.currency)}
                {back && (
                  <span className="ml-2 text-xs text-amber-600">{back}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
